import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePopupRef } from '@framework/hooks';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { useOnEnterCallback } from '@view-model/models/common/hooks/useOnEnterCallback';
import { useTextSelectable } from '@view-model/models/common/hooks';

type Props = {
  onClose(): void;
  initialURL: string | null;
  onSave(url: string | null): void;
  onOpenSubMenu(menuType: string): void;
};

export const HyperLinkMenuButton = forwardRef<
  {
    closePopup: () => void;
  },
  Props
>(({ onClose, initialURL, onSave, onOpenSubMenu }, ref) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [url, setURL] = useState<string | null>(initialURL);
  const [isValid, setIsValid] = useState<boolean>(true);
  const { isOpen, popupRef, handleToggle, handleClose } = usePopupRef<HTMLDivElement>(false);

  useImperativeHandle(ref, () => ({
    closePopup: () => handleClose(),
  }));

  useOnEnterCallback(inputRef, false, () => {
    const valid = validateURL(url);
    setIsValid(valid);
    if (valid) {
      onSave(url);
      onClose();
    }
  });

  const validateURL = (url: string | null): boolean => {
    if (!url || url.trim() === '') {
      return false;
    }

    try {
      const parsedURL = new URL(url);
      return parsedURL.protocol === 'http:' || parsedURL.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onOpenSubMenu('hyperlink');
      handleToggle();
    },
    [handleToggle, onOpenSubMenu]
  );

  const handleChangeURL = (e: React.ChangeEvent<HTMLInputElement>) => {
    const urlStr = e.target.value.trim();
    setURL(urlStr || null);
    const valid = validateURL(urlStr || null);
    setIsValid(valid);
  };

  const handleSaveURL = (url: string | null) => {
    onSave(url);
    onClose();
  };

  const handleClearURL = () => {
    if (inputRef.current?.value) {
      setURL(null);
    }
    onSave(null);
    onClose();
  };

  const handleFocus = () => {
    inputRef.current?.focus();
  };

  const canNotSaveURL = !isValid || !url;
  const canNotClearURL = !initialURL;

  useEffect(() => {
    inputRef.current?.focus();
  }, [isOpen]);

  useTextSelectable(inputRef, isOpen);

  return (
    <div className="relative hover:bg-gray-200">
      <button
        // data-right-click-menu は親 item をクリックしても閉じないようにするためのマーカー
        data-right-click-menu
        className={classNames('flex w-full cursor-pointer px-8 py-4')}
        onClick={handleClick}
      >
        <div className="inline-block pl-4 pr-6">{t('canvas.menu.hyperlink')}</div>
        <FontAwesomeIcon icon={faAngleRight} className="ml-auto" />
      </button>
      <div
        className={classNames(
          'absolute -right-[1380px] top-0 z-50 h-[120px] w-[1380px] rounded-lg bg-white shadow-2xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        <div data-right-click-menu className="flex h-full flex-nowrap p-3">
          <input
            className="m-0 mr-2 w-[760px] flex-auto shrink grow-0 rounded px-3 py-2 text-[44px] leading-tight text-gray-700 shadow"
            defaultValue={url || ''}
            placeholder={t('canvas.hyperlink.linkPlaceholder')}
            type="text"
            ref={inputRef}
            onChange={handleChangeURL}
            onFocus={handleFocus}
          />
          <button
            className="m-0 w-16 flex-auto cursor-pointer p-0 text-[44px] hover:bg-gray-300 disabled:cursor-not-allowed disabled:text-neutral-500"
            onClick={() => handleSaveURL(url)}
            disabled={canNotSaveURL}
          >
            {t('common.action.save')}
          </button>
          <button
            className="m-0 w-16 flex-auto cursor-pointer p-0 text-[44px] hover:bg-gray-300 disabled:cursor-not-allowed disabled:text-neutral-500"
            onClick={handleClearURL}
            disabled={canNotClearURL}
          >
            {t('common.action.delete')}
          </button>
        </div>
      </div>
    </div>
  );
});

HyperLinkMenuButton.displayName = 'HyperLinkMenuButton';
